import { useMemo } from 'react';

import { useTemplates, useTemplatesByIds } from '../../../../hooks/queries';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useParsedIdsFromStrings } from '../../../../hooks/useParsedIdsFromStrings';
import { dedupeBy } from '../../../../utils/dedupeBy';

type UseTemplateOptionsProps = {
  search: string;
  values: string[] | null;
};

export const useTemplateOptions = ({ search, values }: UseTemplateOptionsProps) => {
  const { t } = useTranslation();

  const selectedIds = useParsedIdsFromStrings(values ?? []);
  const {
    templates: selectedTemplates,
    didError: didSelectedTemplatesError,
    isLoading: isSelectedTemplatesLoading,
  } = useTemplatesByIds(selectedIds);
  const {
    templates: suggestedTemplates,
    didError: didSuggestedTemplatesError,
    isLoading: isSuggestedTemplatesLoading,
  } = useTemplates({ search });

  const isLoading = isSelectedTemplatesLoading || isSuggestedTemplatesLoading;
  const didError = didSelectedTemplatesError || didSuggestedTemplatesError;

  const options = useMemo(
    () =>
      dedupeBy([...selectedTemplates, ...suggestedTemplates], 'id').map((template) => ({
        title: template.name,
        subtitle: t('v2.conditionBuilder.templateSubtitle', { id: template.id }),
        value: template.id.toString(),
      })),
    [selectedTemplates, suggestedTemplates, t],
  );

  return { options, didError, isLoading };
};
